import {Component, OnInit} from '@angular/core';
import {AdminIntegrationService} from '../../services/admin/admin-integration.service';
import {AdminCompetitionService} from '../../services/admin/admin-competition.service';
import {
  CompetitionAwardTypes,
  IntegrationAwardModel,
  IntegrationCompetitionAwardModel,
  IntegrationFilter,
  IntegrationModel,
  IntegrationTypes,
  QRCodeContentTypes,
  RewardGrantTypes
} from '../../models/qrcode-integrations/integration.model';
import {CompetitionFilter, CompetitionGoalModel, CompetitionModel} from '../../models/competition/competition.model';
import {ErrorModel} from '../../models/shared/error.model';
import {ModalService} from '../../shared/modals/modal/modal.service';
import {EvoIntegrationQrcodeComponent} from '../../shared/reusable-controls/evo-integration-qrcode/evo-integration-qrcode.component';

export class IntegrationAwardRow {
  award: IntegrationAwardModel;
  competitionId: string = null;
  competitionGoalId: string = null;
  isEmptyRewards = false;
  isExpired = false;
  goalNumber: number = null;
  goalPosition: number = null;
  goal: CompetitionGoalModel = null;
  hasNoRewards = false;
  hasSharedGoal = false;
  hasDefaultName = false;
  displayName = '';
  rewardChips: string[] = [];
  numMoreRewards = 0;

  needsAttention(): boolean {
    return this.hasNoRewards || this.hasSharedGoal || this.hasDefaultName;
  }
}

export class IntegrationCompetitionGroup {
  key: string;
  competitionId: string;
  name: string;
  startAt: Date = null;
  rows: IntegrationAwardRow[] = [];
  numAttention = 0;
  numObstacleCourses = 0;
  goalsLabel = 'No goals';
}

@Component({
  selector: 'app-admin-integrations',
  templateUrl: './admin-integrations.component.html',
  styleUrls: ['./admin-integrations.component.css']
})
export class AdminIntegrationsComponent implements OnInit {

  IntegrationTypes = IntegrationTypes;
  CompetitionAwardTypes = CompetitionAwardTypes;

  categories = [
    {type: IntegrationTypes.competitionAward, title: 'Competition awards', isReserved: false},
    {type: IntegrationTypes.award, title: 'Awards', isReserved: false},
    {type: IntegrationTypes.exam, title: 'Exams', isReserved: true},
    {type: IntegrationTypes.location, title: 'Locations', isReserved: true},
    {type: IntegrationTypes.passcode, title: 'Passcodes', isReserved: true},
    {type: IntegrationTypes.videoHelp, title: 'Video help', isReserved: true}
  ];

  currentType = IntegrationTypes.competitionAward;
  pageSize = 100;
  isLoading = false;
  private loadToken = 0;

  integrations: IntegrationModel[] = [];
  competitions: CompetitionModel[] = [];
  private detailedCompetitionIds: string[] = [];

  // Filters, applied on the loaded awards
  searchString = '';
  competitionFilterId = '';
  awardTypeFilter = '';
  needsAttentionOnly = false;

  // View
  groups: IntegrationCompetitionGroup[] = [];
  numAwards = 0;
  numCompetitionsWithAwards = 0;
  numNeedingAttention = 0;
  numObstacleCourses = 0;
  attentionSummary = '';
  collapsedState = {};
  competitionFilterOptions: {id: string, name: string}[] = [];
  copiedIntegrationId: string = null;

  // Standalone awards, not linked to a competition
  standaloneRows: IntegrationAwardRow[] = [];
  numExpired = 0;

  // Editing, the editor component does the work
  editing: IntegrationCompetitionAwardModel = null;

  constructor(private integrationSv: AdminIntegrationService,
              private competitionSv: AdminCompetitionService,
              private modalSv: ModalService) {
  }

  ngOnInit() {

    this.fetchCompetitions();
    this.reload();

  }

  // Loading

  fetchCompetitions() {

    const compFilter = new CompetitionFilter();
    compFilter.skip = 0;
    compFilter.limit = 200;

    this.competitionSv.fetchCompetitions(compFilter).subscribe((competitions) => {

      this.competitions = competitions;
      this.rebuild();

    });

  }

  reload() {
    this.loadToken += 1;
    this.integrations = [];
    this.isLoading = true;
    this.fetchPage(this.loadToken, 0);
  }

  /**
   * Loads every integration of the current type, page after page, so the overview
   * can group them and check them against each other.
   */
  fetchPage(token: number, skip: number) {

    const filter = new IntegrationFilter();
    filter.skip = skip;
    filter.limit = this.pageSize;
    filter.integrationType = this.currentType;

    this.integrationSv.fetchIntegrations(filter).subscribe((integrations) => {

      if (token !== this.loadToken) {
        return;
      }

      this.integrations = this.integrations.concat(integrations);

      if (integrations.length === this.pageSize) {
        this.fetchPage(token, skip + this.pageSize);
        return;
      }

      this.isLoading = false;
      this.fetchMissingCompetitionDetails();
      this.rebuild();

    }, (err: ErrorModel) => {

      if (token !== this.loadToken) {
        return;
      }

      this.isLoading = false;
      this.modalSv.showTranslatedErrorModal(err);

    });

  }

  /**
   * The competition list might not include goals, so load the competitions that have awards
   * individually to be able to show goal numbers and ranges.
   */
  fetchMissingCompetitionDetails() {

    for (const integration of this.integrations) {

      const award = integration as IntegrationCompetitionAwardModel;
      const competitionId = award.competitionId;

      if (!competitionId || this.detailedCompetitionIds.indexOf(competitionId) >= 0) {
        continue;
      }

      this.detailedCompetitionIds.push(competitionId);
      this.competitionSv.fetchCompetitionById(competitionId).subscribe((competition) => {
        this.replaceCompetition(competition);
        this.rebuild();
      });

    }

  }

  replaceCompetition(competition: CompetitionModel) {

    for (let i = 0; i < this.competitions.length; i++) {
      if (this.competitions[i]._id === competition._id) {
        this.competitions[i] = competition;
        return;
      }
    }

    this.competitions.push(competition);

  }

  // Filters

  onCategoryChanged(type: string) {
    this.currentType = type;
    this.searchString = '';
    this.competitionFilterId = '';
    this.awardTypeFilter = '';
    this.needsAttentionOnly = false;
    this.reload();
  }

  onSearch(searchString: string) {
    this.searchString = searchString;
    this.expandAll();
    this.rebuild();
  }

  onCompetitionFilterChanged(competitionId: string) {
    this.competitionFilterId = competitionId;
    this.rebuild();
  }

  onAwardTypeFilterChanged(awardType: string) {
    this.awardTypeFilter = awardType;
    this.rebuild();
  }

  onNeedsAttentionToggled() {
    this.needsAttentionOnly = !this.needsAttentionOnly;
    this.expandAll();
    this.rebuild();
  }

  onToggleGroup(group: IntegrationCompetitionGroup) {
    this.collapsedState[group.key] = !this.collapsedState[group.key];
  }

  isCollapsed(group: IntegrationCompetitionGroup): boolean {
    return this.collapsedState[group.key] === true;
  }

  expandAll() {
    this.collapsedState = {};
    this.expandedByUser = true;
  }

  private expandedByUser = false;

  // Building the overview

  getCompetition(competitionId: string): CompetitionModel {

    for (const competition of this.competitions) {
      if (competition._id === competitionId) {
        return competition;
      }
    }

    return null;

  }

  getCompetitionName(competitionId: string): string {

    if (!competitionId) {
      return 'Not attached to a competition';
    }

    const competition = this.getCompetition(competitionId);
    if (competition) {
      return competition.name;
    }

    return competitionId;

  }

  isDefaultName(name: string): boolean {
    return /\saward: \d+$/.test(name || '') || /^Goal \d+ award$/.test(name || '');
  }

  buildRow(award: IntegrationAwardModel, goalCounts: {}): IntegrationAwardRow {

    const row = new IntegrationAwardRow();
    row.award = award;

    // Standalone awards have no competition fields
    const competitionAward = award as IntegrationCompetitionAwardModel;
    row.competitionId = competitionAward.competitionId || null;
    row.competitionGoalId = competitionAward.competitionGoalId || null;

    const competition = this.getCompetition(row.competitionId);
    if (competition) {
      for (let i = 0; i < competition.goals.length; i++) {
        if (competition.goals[i]._id === row.competitionGoalId) {
          row.goal = competition.goals[i];
          row.goalPosition = i;
        }
      }
    }

    row.displayName = this.getDisplayName(award, competition);
    row.isEmptyRewards = award.rewards.length === 0;

    // An obstacle course can run without rewards, a plain award without rewards gives nothing.
    row.hasNoRewards = row.isEmptyRewards && award.awardType !== CompetitionAwardTypes.obstacleCourse;
    row.hasDefaultName = this.isDefaultName(award.name);
    row.isExpired = award.hasRedeemDeadlinePassed();

    if (row.competitionId && row.competitionGoalId) {
      row.hasSharedGoal = goalCounts[`${row.competitionId}:${row.competitionGoalId}`] > 1;
    }

    const maxChips = 3;
    for (let i = 0; i < award.rewards.length && i < maxChips; i++) {
      row.rewardChips.push(award.rewards[i].name);
    }

    if (award.rewards.length > maxChips) {
      row.numMoreRewards = award.rewards.length - maxChips;
    }

    return row;

  }

  /**
   * Awards are created as "<competition name> award: <n>". The competition is already shown
   * in the group header, so leave the repeated competition name out of the row.
   */
  getDisplayName(award: IntegrationAwardModel, competition: CompetitionModel): string {

    const name = award.name || '';
    if (!competition || !competition.name || name.indexOf(competition.name) !== 0) {
      return name;
    }

    const shortName = name.substring(competition.name.length).replace(/^[\s\-–:]+/, '');
    if (shortName.length === 0) {
      return name;
    }

    return shortName;

  }

  matchesFilters(row: IntegrationAwardRow): boolean {

    const award = row.award;

    if (this.competitionFilterId && row.competitionId !== this.competitionFilterId) {
      return false;
    }

    if (this.awardTypeFilter && award.awardType !== this.awardTypeFilter) {
      return false;
    }

    if (this.needsAttentionOnly && !row.needsAttention()) {
      return false;
    }

    const search = this.searchString.trim().toLowerCase();
    if (search.length > 0) {

      let found = (award.name || '').toLowerCase().indexOf(search) >= 0;
      for (const reward of award.rewards) {
        if ((reward.name || '').toLowerCase().indexOf(search) >= 0) {
          found = true;
        }
      }

      return found;

    }

    return true;

  }

  rebuild() {

    if (this.currentType === IntegrationTypes.award) {
      this.rebuildStandalone();
      return;
    }

    if (this.currentType !== IntegrationTypes.competitionAward) {
      this.groups = [];
      return;
    }

    const awards = this.integrations as IntegrationCompetitionAwardModel[];

    // How many awards point at the same goal of the same competition
    const goalCounts = {};
    for (const award of awards) {
      if (award.competitionId && award.competitionGoalId) {
        const key = `${award.competitionId}:${award.competitionGoalId}`;
        goalCounts[key] = (goalCounts[key] || 0) + 1;
      }
    }

    const rows: IntegrationAwardRow[] = [];
    for (const award of awards) {
      rows.push(this.buildRow(award, goalCounts));
    }

    this.numberGoalsPerCompetition(rows);
    this.buildSummary(rows);

    // Group the rows that pass the filters by competition
    const groupMap = {};
    const groups: IntegrationCompetitionGroup[] = [];

    for (const row of rows) {

      if (!this.matchesFilters(row)) {
        continue;
      }

      const key = row.competitionId || 'none';
      let group: IntegrationCompetitionGroup = groupMap[key];

      if (!group) {

        group = new IntegrationCompetitionGroup();
        group.key = key;
        group.competitionId = row.competitionId;
        group.name = this.getCompetitionName(row.competitionId);

        const competition = this.getCompetition(row.competitionId);
        if (competition && competition.startAt) {
          group.startAt = competition.startAt;
        }

        groupMap[key] = group;
        groups.push(group);

      }

      group.rows.push(row);

      if (row.needsAttention()) {
        group.numAttention += 1;
      }

      if (row.award.awardType === CompetitionAwardTypes.obstacleCourse) {
        group.numObstacleCourses += 1;
      }

    }

    for (const group of groups) {
      this.sortRows(group);
      this.setGoalsLabel(group);
    }

    this.sortGroups(groups);
    this.setDefaultCollapsed(groups);
    this.groups = groups;

  }

  /**
   * The goal number is visual only and counts per competition, always starting at 1.
   * Awards on the same goal share a number, and the numbering ignores the active filters.
   */
  numberGoalsPerCompetition(rows: IntegrationAwardRow[]) {

    const positionsByCompetition = {};

    for (const row of rows) {

      if (row.goalPosition === null) {
        continue;
      }

      const key = row.competitionId;
      if (!positionsByCompetition[key]) {
        positionsByCompetition[key] = [];
      }

      if (positionsByCompetition[key].indexOf(row.goalPosition) < 0) {
        positionsByCompetition[key].push(row.goalPosition);
      }

    }

    for (const key of Object.keys(positionsByCompetition)) {
      positionsByCompetition[key].sort((a, b) => a - b);
    }

    for (const row of rows) {

      if (row.goalPosition === null) {
        continue;
      }

      const positions = positionsByCompetition[row.competitionId];
      row.goalNumber = positions.indexOf(row.goalPosition) + 1;

    }

  }

  /**
   * Standalone awards are not grouped, there is no competition or goal to group them by.
   */
  rebuildStandalone() {

    const rows: IntegrationAwardRow[] = [];
    for (const integration of this.integrations) {
      rows.push(this.buildRow(integration as IntegrationAwardModel, {}));
    }

    this.buildSummary(rows);

    const visible: IntegrationAwardRow[] = [];
    for (const row of rows) {
      if (this.matchesFilters(row)) {
        visible.push(row);
      }
    }

    visible.sort((a, b) => (a.award.name || '').localeCompare(b.award.name || ''));

    this.groups = [];
    this.standaloneRows = visible;

  }

  buildSummary(rows: IntegrationAwardRow[]) {

    let numNoRewards = 0;
    let numShared = 0;
    let numDefault = 0;
    let numAttention = 0;
    let numObstacle = 0;
    let numExpiredAwards = 0;
    const competitionIds = [];
    const options = [];

    for (const row of rows) {

      if (row.hasNoRewards) {
        numNoRewards += 1;
      }

      if (row.hasSharedGoal) {
        numShared += 1;
      }

      if (row.hasDefaultName) {
        numDefault += 1;
      }

      if (row.needsAttention()) {
        numAttention += 1;
      }

      if (row.award.awardType === CompetitionAwardTypes.obstacleCourse) {
        numObstacle += 1;
      }

      if (row.isExpired) {
        numExpiredAwards += 1;
      }

      const competitionId = row.competitionId || 'none';
      if (competitionIds.indexOf(competitionId) < 0) {
        competitionIds.push(competitionId);
        options.push({id: row.competitionId || '', name: this.getCompetitionName(row.competitionId)});
      }

    }

    const parts = [];
    if (numNoRewards > 0) {
      parts.push(`${numNoRewards} gives no rewards`);
    }

    if (numShared > 0) {
      parts.push(`${numShared} share the same goal`);
    }

    if (numDefault > 0) {
      parts.push(`${numDefault} still have a default name`);
    }

    this.numAwards = rows.length;
    this.numNeedingAttention = numAttention;
    this.numObstacleCourses = numObstacle;
    this.numExpired = numExpiredAwards;
    this.numCompetitionsWithAwards = competitionIds.length;
    this.attentionSummary = parts.join(', ');
    this.competitionFilterOptions = options;

  }

  sortRows(group: IntegrationCompetitionGroup) {

    group.rows.sort((a, b) => {

      const aNumber = a.goalNumber || 9999;
      const bNumber = b.goalNumber || 9999;

      if (aNumber !== bNumber) {
        return aNumber - bNumber;
      }

      return (a.award.name || '').localeCompare(b.award.name || '');

    });

  }

  sortGroups(groups: IntegrationCompetitionGroup[]) {

    groups.sort((a, b) => {

      if (!a.competitionId && b.competitionId) {
        return 1;
      }

      if (a.competitionId && !b.competitionId) {
        return -1;
      }

      let aTime = 0;
      if (a.startAt) {
        aTime = a.startAt.getTime();
      }

      let bTime = 0;
      if (b.startAt) {
        bTime = b.startAt.getTime();
      }

      return bTime - aTime;

    });

  }

  setGoalsLabel(group: IntegrationCompetitionGroup) {

    let min: number = null;
    let max: number = null;

    for (const row of group.rows) {

      if (row.goalNumber === null) {
        continue;
      }

      if (min === null || row.goalNumber < min) {
        min = row.goalNumber;
      }

      if (max === null || row.goalNumber > max) {
        max = row.goalNumber;
      }

    }

    if (min === null) {
      group.goalsLabel = 'No goals';
    } else if (min === max) {
      group.goalsLabel = `Goal ${min}`;
    } else {
      group.goalsLabel = `Goals ${min} – ${max}`;
    }

  }

  /**
   * The first two competitions start open, the rest collapsed, until the user expands or filters.
   */
  setDefaultCollapsed(groups: IntegrationCompetitionGroup[]) {

    for (let i = 0; i < groups.length; i++) {

      const key = groups[i].key;
      if (this.collapsedState[key] === undefined) {
        this.collapsedState[key] = i >= 2 && !this.expandedByUser;
      }

    }

  }

  // Row display helpers

  getRange(row: IntegrationAwardRow): string {

    if (!row.goal) {
      return 'No goal';
    }

    return `${this.formatNumber(row.goal.startValue)} – ${this.formatNumber(row.goal.endValue)}`;

  }

  formatNumber(value: number): string {

    if (value === null || value === undefined) {
      return '0';
    }

    return Number(value).toLocaleString('en-GB');

  }

  getRewardNote(row: IntegrationAwardRow): string {

    if (row.award.rewards.length < 2) {
      return '';
    }

    if (row.award.rewardGrantType === RewardGrantTypes.pickOne) {
      return 'Student picks one';
    }

    return 'All of these';

  }

  isObstacleCourse(integration: IntegrationModel): boolean {
    return (integration as IntegrationAwardModel).awardType === CompetitionAwardTypes.obstacleCourse;
  }

  getRedeemLabel(integration: IntegrationModel): string {

    if (!integration.redeemEndAt) {
      return 'No deadline';
    }

    return this.formatDate(integration.redeemEndAt);

  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
  }

  isReservedCategory(): boolean {

    for (const category of this.categories) {
      if (category.type === this.currentType) {
        return category.isReserved;
      }
    }

    return false;

  }

  // Link, download and print

  onCopyLinkClicked(integration: IntegrationModel) {

    const link = EvoIntegrationQrcodeComponent.qrDataFor(integration);
    if (!link || !navigator.clipboard) {
      this.modalSv.showAlertModal('Could not copy', 'The link could not be copied from this browser.');
      return;
    }

    navigator.clipboard.writeText(link).then(() => {

      this.copiedIntegrationId = integration._id;
      setTimeout(() => {
        this.copiedIntegrationId = null;
      }, 1500);

    });

  }

  getQRCanvas(integration: IntegrationModel): HTMLCanvasElement {

    const holder = document.getElementById('qr-' + integration._id);
    if (!holder) {
      return null;
    }

    return holder.querySelector('canvas');

  }

  fileNameFor(integration: IntegrationModel): string {
    return (integration.name || integration._id).replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  }

  onDownloadQRClicked(integration: IntegrationModel) {

    const canvas = this.getQRCanvas(integration);
    if (!canvas) {
      return;
    }

    const link = document.createElement('a');
    link.download = `${this.fileNameFor(integration)}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();

  }

  onPrintGroupClicked(group: IntegrationCompetitionGroup) {

    let figures = '';
    for (const row of group.rows) {

      const canvas = this.getQRCanvas(row.award);
      if (!canvas) {
        continue;
      }

      const title = this.escapeHtml(row.award.name);
      let goalText = '';
      if (row.goalNumber) {
        goalText = this.escapeHtml(`Goal ${row.goalNumber}`);
      }

      figures += `<figure><img src="${canvas.toDataURL('image/png')}"><figcaption><b>${title}</b><br>${goalText}</figcaption></figure>`;

    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.modalSv.showAlertModal('Pop-up blocked', 'Allow pop-ups for this site to print the QR codes.');
      return;
    }

    printWindow.document.write(
      `<html><head><title>${this.escapeHtml(group.name)}</title><style>
        body { font-family: sans-serif; margin: 24px; }
        h1 { font-size: 20px; }
        .grid { display: flex; flex-wrap: wrap; }
        figure { width: 200px; margin: 0 24px 24px 0; text-align: center; page-break-inside: avoid; }
        img { width: 180px; height: 180px; }
        figcaption { font-size: 13px; }
      </style></head><body><h1>${this.escapeHtml(group.name)}</h1><div class="grid">${figures}</div></body></html>`
    );
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();

  }

  escapeHtml(value: string): string {
    return (value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Editing

  onCreateClicked(competitionId: string = null) {

    const award = new IntegrationCompetitionAwardModel();
    award.integrationType = IntegrationTypes.competitionAward;

    if (this.currentType === IntegrationTypes.award) {
      award.integrationType = IntegrationTypes.award;
    }

    award.contentType = QRCodeContentTypes.url;
    award.visualType = 'qrcode';
    award.numUsesPerStudent = 1;

    if (competitionId) {
      award.competitionId = competitionId;
    } else if (this.competitionFilterId) {
      award.competitionId = this.competitionFilterId;
    }

    this.editing = award;

  }

  onEditClicked(integration: IntegrationModel) {
    this.editing = IntegrationCompetitionAwardModel.generateModel(JSON.parse(JSON.stringify(integration)));
  }

  onCancelEdit() {
    this.editing = null;
  }

  onAwardDeleted() {
    this.editing = null;
    this.modalSv.showAlertModal('Success', 'Award was successfully deleted');
    this.reload();
  }

  onAwardSaved() {
    this.editing = null;
    this.modalSv.showAlertModal('Success', 'Award saved.');
    this.reload();
  }

}
